import { FormProvider, getFormProps, useForm as useConformForm } from "@conform-to/react";
import { getZodConstraint, parseWithZod } from "@conform-to/zod";
import { FormProps, Form as RemixForm, useActionData } from "@remix-run/react";
import { z } from "zod";

export * from "./RadioGroup";
export * from "./Input";
export * from "./FieldError";
export * from "./Label";
export * from "./AddressFields";

export { z };

export function Form({ form, fetcher, ...props }: { form: any; fetcher?: any } & FormProps) {
    const FormComponent = fetcher ? fetcher.Form : RemixForm;
    return (
        <FormProvider context={form.context}>
            <FormComponent {...props} {...getFormProps(form)} />
        </FormProvider>
    );
}

export function useForm<T>(schema: z.ZodTypeAny, options = {}) {
    const lastResult = useActionData();
    return useConformForm<T>({
        constraint: getZodConstraint(schema),
        onValidate({ formData }) {
            return parseWithZod(formData, { schema });
        },
        lastResult,
        ...options,
    });
}

export async function parseInAction<Schema extends z.ZodTypeAny>(
    formData: FormData,
    { schema, ...options }: { schema: Schema | ((intent: string) => Schema) },
): ReturnType<typeof parseWithZod> {
    const submission = await parseWithZod(formData, { schema, async: true, ...options });
    return submission;
}

export function addFormError(ctx: z.RefinementCtx, message: string) {
    ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message,
        path: ["_form"],
    });
    return z.NEVER;
}

export function addFieldError(ctx: z.RefinementCtx, name: string, message: string) {
    ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message,
        path: [name],
    });
}

export function FormError() {
    const action = useActionData();
    return action?.error?.["_form"];
}
